import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isValidHref, isInternalLink } from '../../assets/utils/sidebar/sidebarUtils';

/**
 * 사이드바 서브메뉴 - 탭 스타일
 * 원본: sub-sidebar-menu-tab.html
 *
 * data 구조: { title, menu[], activeHref }
 * menu 구조: [{ label, href?, children[] }]
 */
const SubSidebarMenuTab = ({ data, position = 'left' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const tabContainerRef = useRef(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [sidebarData, setSidebarData] = useState(null);
  const uidRef = useRef(`tab-${Math.random().toString(36).slice(2, 8)}`);

  // 데이터 초기화 및 location.pathname 변경 감지
  useEffect(() => {
    // window.SidebarMenuData를 우선적으로 사용하되, 없으면 data prop 사용
    const sidebarMenuData = window.SidebarMenuData || data || { title: '', menu: [], activeHref: '' };

    // location.pathname이 변경되었을 때 activeHref 업데이트
    const updatedData = {
      ...sidebarMenuData,
      activeHref: location.pathname
    };

    setSidebarData(updatedData);
  }, [data, location.pathname]);

  // 활성 탭 인덱스 계산
  useEffect(() => {
    if (!sidebarData) return;

    // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
    const activeHref = location.pathname;
    const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];

    const hasActiveItem = (group) => {
      if (!group) return false;
      const groupHref = group?.href;
      if (isValidHref(groupHref) && groupHref === activeHref) {
        return true;
      }
      const children = Array.isArray(group?.children) ? group.children : [];
      return children.some((child) => child && child.href === activeHref);
    };

    let foundIndex = 0;
    groups.forEach((group, index) => {
      if (hasActiveItem(group)) {
        foundIndex = index;
      }
    });
    setActiveTabIndex(foundIndex);
  }, [sidebarData, location.pathname]);

  // 탭 전환 핸들러
  const handleTabClick = (index) => {
    setActiveTabIndex(index);
  };

  // 링크 클릭 핸들러
  const handleLinkClick = (e, href) => {
    if (!isValidHref(href)) {
      e.preventDefault();
      return;
    }

    if (isInternalLink(href)) {
      e.preventDefault();
      e.stopPropagation();

      // 현재 활성화된 메뉴 항목을 다시 클릭한 경우 업무화면 영역만 새로고침
      const currentPath = location.pathname;
      if (href === currentPath) {
        // 전역 함수를 통해 페이지 데이터만 다시 로드
        if (typeof window.reloadPageData === 'function') {
          window.reloadPageData();
        }
        return;
      }

      navigate(href);
    }
    // 외부 링크는 기본 동작 허용
  };

  // 사이드바 토글 초기화
  useEffect(() => {
    if (!rootRef.current) return;

    const sidebarRoot = rootRef.current.querySelector('.sidebar-root') || rootRef.current;

    // 원본 JavaScript의 initSidebarToggle 호출
    if (typeof window.SidebarMenuCommon !== 'undefined' &&
        typeof window.SidebarMenuCommon.initSidebarToggle === 'function') {
      window.SidebarMenuCommon.initSidebarToggle(sidebarRoot);
    } else if (typeof window.initSidebarToggle === 'function') {
      window.initSidebarToggle(sidebarRoot);
    }

    // 스크롤 오프셋 조정
    if (typeof window.initSidebarScrollOffset === 'function') {
      window.initSidebarScrollOffset();
    }

    // AOS 초기화
    if (typeof window.AOS !== 'undefined') {
      requestAnimationFrame(() => {
        window.AOS.refreshHard();
      });
    }
  }, [sidebarData]);

  if (!sidebarData) return null;

  const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];
  // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
  const activeHref = location.pathname;
  const uid = uidRef.current;

  return (
    <div
      ref={rootRef}
      className="sidebar-menu sidebar-menu-tab"
      data-aos={position === 'right' ? 'fade-left' : 'fade-right'}
      data-aos-delay="100"
    >
      <div className="sidebar-root">
        <div className="sidebar-title-wrapper">
          <h5 className="sidebar-title">
            <span className="sidebar-title-bar"></span>
            <span className="sidebar-title-text">{sidebarData.title || '섹션 제목'}</span>
            <button
              type="button"
              className="sidebar-toggle-btn"
              id="sidebar-toggle-btn"
              aria-label="사이드바 접기/펼치기"
            >
              <i className="ti-angle-left sidebar-toggle-icon"></i>
            </button>
          </h5>
        </div>
        <div className="tab-menu-container" ref={tabContainerRef} id={uid}>
          <div className="tab-menu-wrapper">
            {/* 탭 헤더 */}
            <div className="tab-menu-headers">
              {groups.map((group, index) => {
                const isActive = index === activeTabIndex;
                const tabId = `${uid}-tab-${index}`;
                return (
                  <button
                    key={index}
                    type="button"
                    className={`tab-menu-header ${isActive ? 'active' : ''}`}
                    data-tab-id={tabId}
                    data-tab-index={index}
                    onClick={() => handleTabClick(index)}
                  >
                    <span className="tab-menu-header-text">{group?.label || ''}</span>
                  </button>
                );
              })}
            </div>

            {/* 탭 컨텐츠 */}
            <div className="tab-menu-contents">
              {groups.map((group, index) => {
                const children = Array.isArray(group?.children) ? group.children : [];
                const isActive = index === activeTabIndex;
                const tabId = `${uid}-tab-${index}`;
                const contentId = `${uid}-content-${index}`;

                const groupHref = group?.href;
                const hasValidGroupHref = isValidHref(groupHref);
                // 자식이 active인 경우에는 부모까지 active 처리하지 않음(중복 하이라이트 방지)
                const hasChildren = children.length > 0;
                const isChildActiveInGroup = hasChildren && children.some((c) => isValidHref(c?.href) && c.href === activeHref);
                const isGroupActive = hasValidGroupHref && groupHref === activeHref && !isChildActiveInGroup;

                return (
                  <div
                    key={index}
                    className={`tab-menu-content ${isActive ? 'active' : ''}`}
                    id={contentId}
                    data-tab-id={tabId}
                  >
                    {/* 그룹 자체에 href가 있는 경우 */}
                    {hasValidGroupHref && (
                      <div className={`tab-menu-group-link ${isGroupActive ? 'active' : ''}`}>
                        {isInternalLink(groupHref) ? (
                          <a
                            href={groupHref}
                            className="tab-menu-link"
                            onClick={(e) => handleLinkClick(e, groupHref)}
                          >
                            <span className="tab-menu-label">{group?.label || ''}</span>
                          </a>
                        ) : (
                          <a href={groupHref} className="tab-menu-link">
                            <span className="tab-menu-label">{group?.label || ''}</span>
                          </a>
                        )}
                      </div>
                    )}

                    {/* 하위 메뉴 목록 */}
                    {children.length > 0 && (
                      <ul className="tab-menu-list">
                        {children.map((child, childIdx) => {
                          const childHref = child?.href;
                          const hasValidChildHref = isValidHref(childHref);
                          const isChildActive = hasValidChildHref && childHref === activeHref;
                          const count = child?.count !== undefined ? child.count : '';

                          return (
                            <li
                              key={childIdx}
                              className={`tab-menu-item ${isChildActive ? 'active' : ''}`}
                            >
                              {hasValidChildHref ? (
                                isInternalLink(childHref) ? (
                                  <a
                                    href={childHref}
                                    className="tab-menu-link"
                                    onClick={(e) => handleLinkClick(e, childHref)}
                                  >
                                    <span className="tab-menu-label">{child?.label || ''}</span>
                                    {count !== '' && (
                                      <span className="tab-menu-count">{count}</span>
                                    )}
                                  </a>
                                ) : (
                                  <a href={childHref} className="tab-menu-link">
                                    <span className="tab-menu-label">{child?.label || ''}</span>
                                    {count !== '' && (
                                      <span className="tab-menu-count">{count}</span>
                                    )}
                                  </a>
                                )
                              ) : (
                                <span className="tab-menu-link">
                                  <span className="tab-menu-label">{child?.label || ''}</span>
                                  {count !== '' && (
                                    <span className="tab-menu-count">{count}</span>
                                  )}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="sidebar-title-line"></div>
      </div>
    </div>
  );
};

export default SubSidebarMenuTab;

